import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { login } from '../redux/slices/authSlice';
import { updateDevoteeInfo } from '../redux/slices/bookingSlice';
import Login from '../components/Login';

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/ykrestapi';

// Guest login API function - parses actual API response format
const guestLogin = async (guestData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Auth/guest-login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        firstName: guestData.firstName.trim(),
        lastName: guestData.lastName.trim(),
        email: guestData.email.trim(),
        phone: guestData.phone.trim()
      })
    });

    console.log('Guest login response status:', response.status);

    const responseData = await response.json();
    console.log('Guest login response data:', responseData);

    // Check API response format: { success, message, data: { token, userId, email, roles, fullName }, statusCode, errors }
    if (!responseData.success) {
      // Extract detailed error message from API response
      let errorMessage = responseData.message || 'Guest login failed';
      
      // Check for validation errors array
      if (responseData.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
        // Join all error messages
        errorMessage = responseData.errors.map(err => err.message || err).join(', ');
      } else if (responseData.errors && typeof responseData.errors === 'object') {
        // Handle errors object format (e.g., { FirstName: ["error"], Email: ["error"] })
        const errorMessages = Object.values(responseData.errors).flat();
        if (errorMessages.length > 0) {
          errorMessage = errorMessages.join(', ');
        }
      }
      
      throw new Error(errorMessage);
    }

    // Validate data exists
    if (!responseData.data || !responseData.data.userId) {
      throw new Error('Invalid response from server');
    }

    // Parse fullName into firstName and lastName
    let firstName = guestData.firstName;
    let lastName = guestData.lastName;
    
    // If API returns fullName, use it as fallback
    if (responseData.data.fullName && (!firstName || !lastName)) {
      const nameParts = responseData.data.fullName.trim().split(' ');
      firstName = firstName || nameParts[0] || '';
      lastName = lastName || nameParts.slice(1).join(' ') || '';
    }

    return {
      success: true,
      token: responseData.data.token,
      user: {
        user_Id: responseData.data.userId,
        userId: responseData.data.userId,
        user_Email: responseData.data.email,
        email: responseData.data.email,
        user_First_Name: firstName,
        user_Last_Name: lastName,
        firstName: firstName,
        lastName: lastName,
        fullName: responseData.data.fullName,
        roles: responseData.data.roles,
        isGuest: true,
        is_Guest: 'Y'
      }
    };

  } catch (error) {
    console.error('Guest login service error:', error);
    return {
      success: false,
      error: error.message || 'Guest login failed. Please try again.'
    };
  }
};

// Form validation function
const validateGuestLoginForm = (formData) => {
  const errors = {};
  
  // First Name validation
  if (!formData.firstName?.trim()) {
    errors.firstName = 'First name is required';
  } else if (formData.firstName.trim().length < 2) {
    errors.firstName = 'First name must be at least 2 characters';
  }
  
  // Last Name validation
  if (!formData.lastName?.trim()) {
    errors.lastName = 'Last name is required';
  } else if (formData.lastName.trim().length < 2) {
    errors.lastName = 'Last name must be at least 2 characters';
  }
  
  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!formData.email?.trim()) {
    errors.email = 'Email is required';
  } else if (!emailRegex.test(formData.email)) {
    errors.email = 'Please enter a valid email address';
  }
  
  // Phone validation
  const phoneDigits = formData.phone?.replace(/\D/g, '') || '';
  if (!formData.phone?.trim()) {
    errors.phone = 'Phone number is required';
  } else if (phoneDigits.length !== 10) {
    errors.phone = 'Phone number must be exactly 10 digits';
  } else if (!/^[2-9]\d{9}$/.test(phoneDigits)) {
    errors.phone = 'Please enter a valid US phone number';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

const GuestLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const redirectPath = location.state?.redirectPath || '/';
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [showLogin, setShowLogin] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const validation = validateGuestLoginForm({ ...formData, [name]: value });
    
    if (validation.errors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: validation.errors[name]
      }));
    }
  };

  const validateForm = () => {
    const validation = validateGuestLoginForm(formData);
    setValidationErrors(validation.errors);
    return validation.isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      setError('Please fix all validation errors before submitting.');
      return;
    }

    setLoading(true);

    try {
      const guestLoginData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone
      };

      console.log('Guest login data being sent:', guestLoginData);

      const result = await guestLogin(guestLoginData);

      if (!result.success) {
        throw new Error(result.error);
      }

      console.log('Guest login response data:', result);

      // Store user data and token using the same auth slice as normal login
      const authPayload = {
        email: formData.email,
        entity: 'RMS',
        userData: {
          ...result.user,
          token: result.token
        }
      };

      dispatch(login(authPayload));

      // Also save guest info to devoteeInfo for use in booking flow
      dispatch(updateDevoteeInfo({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone
      }));

      console.log('Guest info saved to devoteeInfo:', { 
        firstName: formData.firstName, 
        lastName: formData.lastName, 
        email: formData.email, 
        phone: formData.phone 
      });

      // Navigate to the same dashboard as normal login
      navigate(redirectPath);

    } catch (error) {
      console.error('Guest login error:', error);
      const errorMessage = error.message || 'Please try again.';
      
      // Provide helpful message for existing users
      if (errorMessage.includes('Guest login failed') || errorMessage.includes('500')) {
        setError('This email is already registered. Please use the regular login with your password, or contact support if you forgot your password.');
      } else {
        setError(`Guest login failed: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  if (showLogin) {
    return (
      <Login
        onLoginSuccess={(data) => {
          setShowLogin(false);
          navigate(redirectPath);
        }}
        onCancel={() => setShowLogin(false)}
        entity="RMS"
        redirectPath={redirectPath}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn overflow-hidden">
      {/* Minimalist Background - matching Login component */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Subtle geometric pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(0,0,0,.05) 35px, rgba(0,0,0,.05) 70px)`
        }}></div>
      </div>

      {/* Clean White Card - matching Login component styling */}
      <div className="relative bg-white rounded-2xl shadow-[0_20px_70px_rgba(0,0,0,0.08)] max-w-md w-full p-10 animate-slideInUp border border-gray-100">
        {/* Minimal top accent */}
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-black to-transparent"></div>

        {/* Clean Header */}
        <div className="text-center mb-10 relative">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-black rounded-2xl shadow-xl mb-5 transform hover:scale-105 transition-all duration-300">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight">
            Continue as Guest
          </h2>
          <p className="text-gray-500 text-sm">Enter your details to continue</p>
        </div>

        {/* Guest Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* First Name */}
          <div className="relative">
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              onBlur={handleBlur}
              className={`w-full px-4 py-4 bg-white border-2 rounded-xl focus:outline-none transition-all duration-200 text-base text-gray-900 placeholder-gray-400 ${
                validationErrors.firstName
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-gray-200 focus:border-black focus:ring-2 focus:ring-gray-100'
              }`}
              placeholder="First Name"
              disabled={loading}
            />
            {validationErrors.firstName && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.firstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div className="relative">
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              onBlur={handleBlur}
              className={`w-full px-4 py-4 bg-white border-2 rounded-xl focus:outline-none transition-all duration-200 text-base text-gray-900 placeholder-gray-400 ${
                validationErrors.lastName
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-gray-200 focus:border-black focus:ring-2 focus:ring-gray-100'
              }`}
              placeholder="Last Name"
              disabled={loading}
            />
            {validationErrors.lastName && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.lastName}</p>
            )}
          </div>

          {/* Email */}
          <div className="relative">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={handleBlur}
              className={`w-full px-4 py-4 bg-white border-2 rounded-xl focus:outline-none transition-all duration-200 text-base text-gray-900 placeholder-gray-400 ${
                validationErrors.email
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-gray-200 focus:border-black focus:ring-2 focus:ring-gray-100'
              }`}
              placeholder="Email Address"
              disabled={loading}
            />
            {validationErrors.email && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
            )}
          </div>

          {/* Phone */}
          <div className="relative">
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              onBlur={handleBlur}
              className={`w-full px-4 py-4 bg-white border-2 rounded-xl focus:outline-none transition-all duration-200 text-base text-gray-900 placeholder-gray-400 ${
                validationErrors.phone
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-gray-200 focus:border-black focus:ring-2 focus:ring-gray-100'
              }`}
              placeholder="Phone Number"
              disabled={loading}
            />
            {validationErrors.phone && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.phone}</p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4 animate-shake">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-6">
            <button
              type="button"
              onClick={() => navigate('/events')}
              disabled={loading}
              className="flex-1 bg-white border-2 border-gray-200 text-gray-700 font-semibold py-4 px-4 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Back
            </button>
            
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-black text-white font-bold py-4 px-4 rounded-xl hover:bg-gray-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Continuing...
                </span>
              ) : (
                'Continue as Guest'
              )}
            </button>
          </div>

          {/* Divider */}
          <div className="relative py-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500 font-medium">Have an account?</span>
            </div>
          </div>

          {/* Login Link */}
          <button
            type="button"
            onClick={() => setShowLogin(true)}
            className="w-full py-4 px-6 bg-white border-2 border-gray-200 text-gray-900 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
          >
            Sign In Instead
          </button>
        </form>
      </div>
    </div>
  );
};

export default GuestLogin;
